import type { UserDetailsType } from '@store/userDetails/types';
import type {
  ChangedFieldPathToken,
  ChangeTrackingSource,
} from '@typeDefinitions/changeTrackingTypes';

import { CHANGE_TRACKING_SOURCES } from '../../utils/changeTrackingRegistry';
import { createFormChangeTracker } from '../../utils/createFormChangeTracker';
import { findChangedForms } from '../../utils/findChangedForms';

const IGNORED_FIELDS = new Set([
  'availableRoles',
  'dateCreated',
  'groups',
  'officeSettings',
  'offices',
  'operationType',
  'picture',
  'savedSearches',
  'thumbnail',
]);

const PROFILE_FIELDS = new Set([
  'accountingGroup',
  'active',
  'dormantCustomerEnabled',
  'email',
  'fax',
  'firstName',
  'lastName',
  'name',
  'office',
  'officeId',
  'officeStartDate',
  'percentage',
  'phone1',
  'phone1Ext',
  'phone2',
  'phone2Ext',
  'postingGroupId',
  'settings',
  'thirdPartyLinked',
]);

const isEmptyFormValue = (value: unknown): boolean =>
  value === null ||
  value === undefined ||
  value === '' ||
  (Array.isArray(value) && value.length === 0);

const getPropertyPath = (path: readonly ChangedFieldPathToken[]): string[] =>
  path.flatMap((token) => (token.type === 'property' ? [token.key] : []));

const areDatesEqual = (before: string, after: string): boolean => {
  const beforeDate = new Date(before);
  const afterDate = new Date(after);

  return (
    !Number.isNaN(beforeDate.getTime()) &&
    !Number.isNaN(afterDate.getTime()) &&
    beforeDate.toDateString() === afterDate.toDateString()
  );
};

const areStringArraysEqualIgnoringOrder = (
  before: unknown,
  after: unknown,
): boolean => {
  if (!Array.isArray(before) || !Array.isArray(after)) return false;
  if (
    !before.every((value) => typeof value === 'string') ||
    !after.every((value) => typeof value === 'string')
  ) {
    return false;
  }

  const beforeValues = [...before].sort();
  const afterValues = [...after].sort();

  return (
    beforeValues.length === afterValues.length &&
    beforeValues.every((value, index) => value === afterValues[index])
  );
};

const areUserDetailsScalarValuesEqual = (
  before: unknown,
  after: unknown,
  path: readonly ChangedFieldPathToken[],
): boolean => {
  if (Object.is(before, after)) return true;
  if (isEmptyFormValue(before) && isEmptyFormValue(after)) return true;

  const field = getPropertyPath(path).at(-1) ?? '';

  if (
    (field === 'roles' || field === 'permissions') &&
    areStringArraysEqualIgnoringOrder(before, after)
  ) {
    return true;
  }

  if (typeof before === 'number' && typeof after === 'string') {
    return after.trim() !== '' && Number(after) === before;
  }

  if (typeof before === 'string' && typeof after === 'number') {
    return before.trim() !== '' && Number(before) === after;
  }

  if (typeof before === 'string' && typeof after === 'string') {
    if (/date/i.test(field) && areDatesEqual(before, after)) {
      return true;
    }

    if (/phone|fax/i.test(field)) {
      return before.replace(/\D/g, '') === after.replace(/\D/g, '');
    }
  }

  return false;
};

const getUserDetailsForm = (
  path: readonly ChangedFieldPathToken[],
): ChangeTrackingSource => {
  const [field] = getPropertyPath(path);

  if (field === 'roles') {
    return CHANGE_TRACKING_SOURCES.userDetails.roles;
  }

  if (field === 'permissions') {
    return CHANGE_TRACKING_SOURCES.userDetails.permissions;
  }

  if (PROFILE_FIELDS.has(field)) {
    return CHANGE_TRACKING_SOURCES.userDetails.profileDetails;
  }

  return CHANGE_TRACKING_SOURCES.userDetails.profileDetails;
};

const findUserDetailsChangedForms = (
  before: UserDetailsType,
  after: UserDetailsType,
): ChangeTrackingSource[] =>
  findChangedForms(before, after, {
    ignoredFields: IGNORED_FIELDS,
    areScalarValuesEqual: areUserDetailsScalarValuesEqual,
    getForm: (change) => getUserDetailsForm(change.path),
  });

const userDetailsFormChangeTracker = createFormChangeTracker<
  UserDetailsType,
  ChangeTrackingSource
>({
  getIdentity: (user) => user.id || undefined,
  findChangedForms: findUserDetailsChangedForms,
});

export const setUserDetailsChangeBaseline =
  userDetailsFormChangeTracker.setBaseline;
export const prepareUserDetailsChangeSummary =
  userDetailsFormChangeTracker.prepare;
